import { checkVersion } from "../api/version";

const VERSION_CHECK_INTERVAL = 15 * 60 * 1000; // 15 minutes in milliseconds

export class VersionCheckService {
  constructor() {
    this.currentVersion = import.meta.env.VITE_APP_VERSION;
    this.checkInterval = null;
    this.isChecking = false;
  }

  start() {
    // Never run against the dev server. `VITE_APP_VERSION` is the placeholder
    // "1.0.0" from .env locally, while GET /version returns whatever CI last
    // deployed (deployment.yaml builds it as "<branch>-<sha>"), so the two can
    // never match and every localhost load popped "New Version Available".
    // There is nothing to update to in dev either — the running code is the
    // newest. Built bundles are unaffected: import.meta.env.DEV is false there,
    // including `npm run preview`.
    if (import.meta.env.DEV) return;

    // Check immediately on start
    this.checkVersion();

    // Set up interval for periodic checks
    this.checkInterval = setInterval(() => {
      this.checkVersion();
    }, VERSION_CHECK_INTERVAL);
  }

  stop() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  async checkVersion() {
    if (this.isChecking) return;
    this.isChecking = true;

    try {
      const versionInfo = await checkVersion();

      if (versionInfo?.version && versionInfo.version !== this.currentVersion) {
        this.showUpdateNotification(versionInfo);
      }
    } catch (error) {
      console.error("Failed to check version:", error);
    } finally {
      this.isChecking = false;
    }
  }

  showUpdateNotification(versionInfo) {
    // Create notification element
    const notification = document.createElement("div");
    // Named so the /start onboarding can keep it from covering its UI.
    notification.id = "version-update-toast";
    notification.className =
      "fixed top-4 right-4 z-50 bg-white rounded-lg shadow-lg p-4 max-w-md border border-gray-200";
    notification.innerHTML = `
      <div class="flex items-start">
        <div class="shrink-0">
          <svg class="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-gray-900">New Version Available</h3>
          <div class="mt-2 text-sm text-gray-500">
            <p>A new version of the application is available. Please update to continue using the latest features.</p>
            ${versionInfo.changes ? `<p class="mt-2"><strong>Changes:</strong> ${versionInfo.changes}</p>` : ""}
          </div>
          <div class="mt-4">
            <button onclick="window.location.reload(true)" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-xs text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
              Update Now
            </button>
          </div>
        </div>
      </div>
    `;

    // Add to document
    document.body.appendChild(notification);

    // Remove after 30 seconds
    setTimeout(() => {
      notification.remove();
    }, 30000);
  }
}

// Create a singleton instance
export const versionCheckService = new VersionCheckService();
